import { Button, DataTable, FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import { isModuleName, ModuleName } from '@/modules/companies'
import { downloadFile } from '@/utils/file'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'
import type { UserExportNode } from '../types.graphql'
import { useGetExportStatus } from '../util'
import { UserExportProgress } from './UserExportProgress'

interface UserExportsTableProps {
  userExports: UserExportNode[]
  loading: boolean
  isFirstLoad: boolean
  lastVisit: Date | undefined
}

export const UserExportsTable: React.FC<UserExportsTableProps> = ({
  userExports,
  loading,
  isFirstLoad,
  lastVisit,
}) => {
  const translations = useTranslate({
    noResults: 'common.no-results',
    name: 'common.name',
    time: 'common.time',
    download: 'common.download',
    module: 'common.module',
    progress: 'exports.progress',
    salary: 'common.salary',
    users: 'common.users',
    pending: 'exports.status.pending',
    error: 'exports.status.error',

    activities: 'common.activities',
    absences: 'common.absences',
    forum: 'common.forum',
    handbook: 'common.handbook',
    infringements: 'common.infringements',
    issues: 'common.issues',
    terminal: 'common.terminal',
    timesheets: 'common.timesheets',
    vehicles: 'common.vehicle',
    routePlanner: 'common.route-planner',
    formCreator: 'common.formcreator',
  })

  const [xhrLoading, setXhrLoading] = useState(false)

  const getModuleNames = useCallback(
    (name: string | null) => {
      if (!isModuleName(name ?? '')) return '?'

      switch (name) {
        case ModuleName.ACTIVITIES:
          return translations.activities
        case ModuleName.ABSENCES:
          return translations.absences
        case ModuleName.FORUM:
          return translations.forum
        case ModuleName.HANDBOOK:
          return translations.handbook
        case ModuleName.INFRINGEMENTS:
          return translations.infringements
        case ModuleName.ISSUES:
          return translations.issues
        case ModuleName.TERMINAL:
          return translations.terminal
        case ModuleName.TIMESHEETS:
          return translations.timesheets
        case ModuleName.VEHICLES:
          return translations.vehicles
        case ModuleName.ROUTEPLANNER:
          return translations.routePlanner
        case ModuleName.SALARY:
          return translations.salary
        case ModuleName.FORMCREATOR:
          return translations.formCreator
        case ModuleName.USERS:
          return translations.users
        default:
          return '?'
      }
    },
    [
      translations.absences,
      translations.activities,
      translations.formCreator,
      translations.forum,
      translations.handbook,
      translations.infringements,
      translations.issues,
      translations.routePlanner,
      translations.salary,
      translations.terminal,
      translations.timesheets,
      translations.users,
      translations.vehicles,
    ]
  )

  const getExportStatus = useGetExportStatus()

  const handleDownload = useCallback((link: string) => {
    return async () => {
      try {
        setXhrLoading(true)
        await downloadFile(link)
      } finally {
        setXhrLoading(false)
      }
    }
  }, [])

  const column = createColumnHelper<UserExportNode>()

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        cell(info) {
          const name = info.getValue()
          const status = getExportStatus(info.row.original)
          const bold = !!lastVisit
            ? parseISO(info.row.original.createdAt) > lastVisit
            : false

          return !!name ? (
            <span className={cn(bold && 'font-bold')}>{name}</span>
          ) : (
            <span className="no-name">
              {status.status === 'pending'
                ? translations.pending
                : status.status === 'error'
                ? translations.error
                : '?'}
            </span>
          )
        },
      }),
      column.accessor('createdAt', {
        header: translations.time,
        cell(info) {
          const date = parseISO(info.getValue())
          return format(date, 'dd.MM.yyyy HH:mm')
        },
      }),
      column.accessor('module', {
        header: translations.module,
        cell(info) {
          const moduleName = getModuleNames(info.getValue())
          return moduleName
        },
      }),
      column.accessor('progress', {
        header: translations.progress,
        meta: { narrow: true, center: true },
        cell(info) {
          const status = getExportStatus(info.row.original)
          return <UserExportProgress status={status} />
        },
      }),
      column.display({
        id: 'download',
        header: translations.download,
        meta: { narrow: true, center: true },
        cell(info) {
          const status = getExportStatus(info.row.original)
          if (status.status !== 'success') return null

          return (
            <Button
              variant="outline"
              size="lg"
              disabled={xhrLoading}
              onClick={handleDownload(status.message)}
            >
              <FAIcon icon="download" />
              <span>{translations.download}</span>
            </Button>
          )
        },
      }),
    ],
    [
      column,
      getExportStatus,
      getModuleNames,
      handleDownload,
      lastVisit,
      translations.download,
      translations.error,
      translations.module,
      translations.name,
      translations.pending,
      translations.progress,
      translations.time,
      xhrLoading,
    ]
  )

  return (
    <DataTable
      columns={tableColumns}
      data={userExports}
      inCard
      loading={loading && isFirstLoad}
    />
  )
}
